import { memo, useEffect, useMemo, useState, useRef } from 'react'
import { cn } from '@lib/utils'

/**
 * Componente Blinker que renderiza un cursor parpadeante.
 * @component
 * @returns {JSX.Element} Un elemento de cursor parpadeante.
 */
const Blinker = () => {
  const [show, setShow] = useState(true)

  useEffect(() => {
    const interval = setInterval(() => {
      setShow(prev => !prev)
    }, 150)
    return () => clearInterval(interval)
  }, [])

  return (
    <span
      className={show ? '' : 'opacity-0'}
      aria-hidden='true' // Ocultar el cursor de los lectores de pantalla
    >
      |
    </span>
  )
}

/**
 * Componente SmoothEffect para animaciones de escritura suave.
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {string[]} props.words - Palabras para mostrar.
 * @param {number} props.index - Índice actual de escritura.
 * @param {number} props.alwaysVisibleCount - Número de caracteres siempre visibles.
 * @param {string[]} props.highlightWords - Palabras a destacar con estilo especial.
 * @returns {JSX.Element} La animación de efecto suave.
 */
const SmoothEffect = memo(({ words, index, alwaysVisibleCount, highlightWords = [] }) => (
  <div
    className='flex flex-wrap whitespace-pre'
    aria-label={words.join(' ')} // Proporcionar el texto completo para lectores de pantalla
    role='text'
  >
    {words.map((word, wordIndex) => {
      const isHighlighted = highlightWords.includes(word.toLowerCase())

      return (
        <span
          key={wordIndex}
          className={cn('transition-all duration-300 ease-in-out', {
            'opacity-100': wordIndex < index,
            'opacity-0': wordIndex >= index + alwaysVisibleCount,
            'font-bold transition-transform': isHighlighted,
            'hover:text-primary-500 hover:scale-110': isHighlighted && wordIndex < index
          })}
          aria-hidden={wordIndex >= index + alwaysVisibleCount} // Ocultar palabras no visibles
        >
          {word}
          {wordIndex < words.length && <span aria-hidden='true'>&nbsp;</span>}
        </span>
      )
    })}
  </div>
))

SmoothEffect.displayName = 'SmoothEffect'

/**
 * Componente NormalEffect para animaciones de escritura estándar.
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {string} props.text - Texto para mostrar.
 * @param {number} props.index - Índice actual de escritura.
 * @param {number} props.alwaysVisibleCount - Número de caracteres siempre visibles.
 * @param {string[]} props.highlightWords - Palabras a destacar con estilo especial.
 * @returns {JSX.Element} La animación de efecto normal.
 */
const NormalEffect = memo(({ text, index, alwaysVisibleCount, highlightWords = [] }) => {
  const visibleText = text.slice(0, Math.max(index, Math.min(text.length, alwaysVisibleCount ?? 1)))

  if (!highlightWords.length) {
    return <>{visibleText}</>
  }

  const words = visibleText.split(/(\s+)/)

  return (
    <span className='inline-flex flex-wrap' role='text'>
      {words.map((word, idx) => {
        const trimmedWord = word.trim().toLowerCase()
        const isHighlighted = trimmedWord && highlightWords.includes(trimmedWord)

        return (
          <span
            key={idx}
            className={cn({
              'inline-block font-bold': isHighlighted, // Removido scale-110
              'hover:text-primary-50 cursor-none touch-none font-extrabold leading-5 text-blue-400 transition-all ease-in-out hover:scale-110 transform-gpu':
                isHighlighted,
              'whitespace-pre': word.match(/\s+/)
            })}
          >
            {word}
          </span>
        )
      })}
    </span>
  )
})

NormalEffect.displayName = 'NormalEffect'

/**
 * Componente CursorWrapper que gestiona la visibilidad del cursor.
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.visible - Si el cursor es visible.
 * @param {boolean} props.waiting - Si el cursor está en modo espera.
 * @param {React.ReactNode} props.children - Contenido del cursor.
 * @returns {JSX.Element|null} El elemento del cursor o null.
 */
const CursorWrapper = memo(({ visible, waiting, children }) => {
  const [on, setOn] = useState(true)

  useEffect(() => {
    if (!visible) return () => {}

    const interval = setInterval(() => {
      setOn(prev => !prev)
    }, 500)

    return () => clearInterval(interval)
  }, [visible])

  if (!visible || (!on && !waiting)) return null

  return (
    <span role='presentation' aria-hidden='true'>
      {children}
    </span>
  )
})
CursorWrapper.displayName = 'CursorWrapper'

/**
 * Componente Type que maneja la animación de escritura.
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {string} props.text - Texto a mostrar.
 * @param {boolean} props.repeat - Si la animación se repite.
 * @param {React.ReactNode} props.cursor - Elemento de cursor.
 * @param {number} props.delay - Retardo entre cada carácter.
 * @param {boolean} props.grow - Si el contenedor crece con el texto.
 * @param {string} props.className - Clases adicionales para el contenedor.
 * @param {number} props.alwaysVisibleCount - Número de caracteres siempre visibles.
 * @param {boolean} props.smooth - Si la animación es suave.
 * @param {number} [props.waitTime=1000] - Tiempo de espera entre repeticiones.
 * @param {Function} props.onComplete - Función a ejecutar al completar la animación.
 * @param {boolean} props.hideCursorOnComplete - Si se oculta el cursor al completar la animación.
 * @param {string[]} props.highlightWords - Palabras a destacar con estilo especial.
 * @returns {JSX.Element} Contenedor de la animación de escritura.
 */
const Type = ({
  text,
  repeat,
  cursor,
  delay,
  grow,
  className,
  alwaysVisibleCount,
  smooth,
  waitTime = 5000,
  onComplete,
  hideCursorOnComplete,
  highlightWords = []
}) => {
  const [index, setIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  const [isComplete, setIsComplete] = useState(false)

  const timerRef = useRef(null)
  const intervalRef = useRef(null)

  const normalizedHighlightWords = useMemo(() => highlightWords.map(word => word.toLowerCase()), [highlightWords])

  const words = useMemo(() => text.split(/\s+/), [text])
  const total = useMemo(() => (smooth ? words.length : text.length), [smooth, words, text])

  const showCursor = useMemo(() => {
    if (!cursor || smooth) return false
    if (isComplete && hideCursorOnComplete) return false
    return true
  }, [cursor, smooth, isComplete, hideCursorOnComplete])

  useEffect(() => {
    if (repeat || !isComplete) {
      intervalRef.current = setInterval(() => {
        setIndex(prev => {
          if (!repeat && prev >= total) return total
          return prev + direction
        })
      }, delay)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [total, direction, delay, repeat, isComplete])

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    if (repeat) {
      if (index >= total) {
        timerRef.current = setTimeout(() => setDirection(-1), waitTime)
      }

      if (index <= 0) {
        timerRef.current = setTimeout(() => setDirection(1), waitTime)
      }
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [index, total, repeat, waitTime])

  useEffect(() => {
    if (index >= total && !repeat && !isComplete) {
      setIsComplete(true)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      onComplete?.()
    }
  }, [index, total, repeat, isComplete, onComplete])

  return (
    <div
      className={cn('relative w-full font-mono', className)}
      role='region'
      aria-live='polite' // Anunciar cambios dinámicos
      aria-atomic='true' // Asegurar que se lea todo el contenido
    >
      {!grow && <div className='invisible'>{text}</div>}
      <div className={cn({ 'absolute inset-0 h-full w-full': !grow })}>
        <NormalEffect
          text={text}
          index={index}
          alwaysVisibleCount={alwaysVisibleCount ?? 1}
          highlightWords={normalizedHighlightWords}
        />

        {showCursor && (
          <CursorWrapper waiting={index === total || index === 0} visible={true}>
            {cursor}
          </CursorWrapper>
        )}
      </div>
    </div>
  )
}
Type.displayName = 'Type'

export const TypingText = memo(
  ({
    text,
    repeat = false,
    cursor = <Blinker />,
    delay = 32,
    className,
    grow = true,
    alwaysVisibleCount = 1,
    smooth = false,
    waitTime,
    onComplete,
    hideCursorOnComplete = true,
    highlightWords = [],
    ...props
  }) => (
    <Type
      key={text}
      delay={delay}
      waitTime={waitTime ?? 1000}
      grow={grow}
      repeat={repeat}
      text={text}
      cursor={cursor}
      className={className}
      smooth={smooth}
      alwaysVisibleCount={alwaysVisibleCount}
      onComplete={onComplete}
      hideCursorOnComplete={hideCursorOnComplete}
      highlightWords={highlightWords}
      {...props}
    />
  ),
  (prevProps, nextProps) =>
    prevProps.text === nextProps.text &&
    JSON.stringify(prevProps.highlightWords) === JSON.stringify(nextProps.highlightWords)
)

TypingText.displayName = 'TypingText'

export default TypingText
